
import Vue from 'vue';
import Chart from '@/services/Chart';
import EventService from '@/services/EventService';
import {mapState} from 'vuex';
import axios from 'axios';
import {saveAs} from 'file-saver';
import store, {
  GetScopeFromKey,
  ConfSortByType, // TODO (ohtsuchi) 2025年以降 コードを削除
  ConfSortByValue, // TODO (ohtsuchi) 2025年以降 コードを削除
  ScopeKey,
  Scope,
} from '../store';
import Breadcrumbs from '../components/breadcrumbs.vue';
import {ChartOutputType, ChartSortTrainings} from '@/services/types';
import type {User} from 'firebase/auth';
import {TrainingCategory, TrainingCategoryDefault} from '@/components/types';

type UserData = Array<string>;

const scopeDescriptions = new Map<Scope, string>([
  ['truck', 'トラック'],
  ['forklift', 'フォークリフト'],
]);

const mainDescriptions = new Map<boolean, string>([
  [true, '12項目'],
  [false, 'ヒヤリハット'],
]);

type OutputTypeOption = {text: string; value: ChartOutputType};

function convertForbiddenFilenameCharToFullWidth(input: string): string {
  const FULLWIDTH_MAP: Record<string, string> = {
    '\\': '＼',
    '/': '／',
    ':': '：',
    '*': '＊',
    '?': '？',
    '"': '＂',
    '<': '＜',
    '>': '＞',
    '|': '｜',
  };
  // 正規表現で対象文字をまとめて検索し、マップに従って置換する
  return input.replace(/[\\/:*?"<>|]/g, (match: string) => {
    return FULLWIDTH_MAP[match] || match;
  });
}

function isSelectedCategory(header: Audit, filterTag: string) {
  return header.tags.some(tag => tag.key === filterTag);
}

type Audit = {
  status: string;
  title: string;
  deadline: string;
  id: string;
  isActive: boolean;
  safetyEducationIndex: number;
  tags: TrainingCategory[];
};

type Data = {
  viewportHeight: number;
  start: string;
  end: string;
  report: Array<Array<string>>;
  autits: Array<Audit>;
  isLoading: boolean;
  snackbar: boolean;
  snackbarMessage: string;
  snackbarColor: string;
  chartSortBy: ChartSortTrainings;
  outputTypes: Array<OutputTypeOption>;
  outputType: ChartOutputType;
  isMounted: boolean;
  allTags: TrainingCategory[];
  filterTag: string;
};

type Method = {
  indexFormat(src: number): string;
  download(): Promise<void>;
  updateViewportHeight(): void;
  getSortByDefault(): ChartSortTrainings;
  init(): Promise<void>;
  categoryDesc(): string;
  outputTypeDesc(): string;
};

type Computed = {
  tableHeight: number;
  isNoSubGroup: boolean;
  branchOfficeName: string;
  boGroupName: string;
  user: User | undefined;
  isGroup: boolean;
  accountName: string;
  currentName: string;
  currentScope: ScopeKey;
  noSubGroupStartYear: number;
  sortBy: ConfSortByType;
  canShowFilterTag: boolean;
  filteredHeaders: Audit[];
  filteredMatrix: string[][];
};

type Props = {
  year: string;
  main: string;
};

const baseURL = EventService.ProtoURL();
const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(config => {
  const companyID = store.state.current;
  if (companyID !== '') {
    config.headers['x-company-id'] = companyID;
  }
  return config;
});

export default Vue.extend<Data, Method, Computed, Props>({
  components: {
    Breadcrumbs,
  },
  name: 'training-chart',
  props: {
    year: {
      type: String,
      default: '',
    },
    main: {
      type: String,
      default: 'true',
    },
  },
  computed: {
    tableHeight(): number {
      return this.viewportHeight - 220;
    },
    isNoSubGroup: function (): boolean {
      return (
        this.noSubGroupStartYear != 0 &&
        Number(this.year) >= this.noSubGroupStartYear
      );
    },
    branchOfficeName(): string {
      if (!this.isGroup) {
        return convertForbiddenFilenameCharToFullWidth(this.accountName);
      }
      return convertForbiddenFilenameCharToFullWidth(this.currentName);
    },
    boGroupName(): string {
      if (!this.isGroup) {
        return '';
      }
      return convertForbiddenFilenameCharToFullWidth(this.accountName);
    },
    canShowFilterTag(): boolean {
      return !this.main;
    },
    filteredHeaders(): Audit[] {
      if (this.main) {
        return this.autits;
      }
      console.log('filterTag', this.filterTag);
      const filteredHeaders = this.autits.filter((header: Audit) => {
        return isSelectedCategory(header, this.filterTag);
      });
      const ret = [this.autits[0], ...filteredHeaders];
      console.log('filteredHeaders', ret);
      return ret;
    },
    filteredMatrix(): string[][] {
      if (this.main) {
        return this.report;
      }
      const filteredHeaderIndexes = this.autits.reduce<number[]>(
        (indexes: number[], header: Audit, index: number) => {
          if (isSelectedCategory(header, this.filterTag)) {
            indexes.push(index);
          }
          return indexes;
        },
        [0] as number[]
      );
      console.log('filteredHeaderIndexes', filteredHeaderIndexes);
      const ret = this.report.map((row: string[]) => {
        return filteredHeaderIndexes.map((index: number) => {
          return row[index];
        });
      });
      console.log('filteredMatrix', ret);
      return ret;
    },
    ...mapState([
      'user',
      'isGroup',
      'accountName',
      'currentName',
      'currentScope',
      'noSubGroupStartYear',
      'sortBy', // TODO (ohtsuchi) 2025年以降 コードを削除
    ]),
  },
  methods: {
    indexFormat(src: number): string {
      return EventService.getSafetyEducationIndexFormat(src);
    },
    async download() {
      if (this.user) {
        try {
          this.isLoading = true;
          const token = await this.user.getIdToken();
          const scope = GetScopeFromKey(this.currentScope);
          const {data} = await EventService.downloadChartInfo(
            token,
            this.year,
            Boolean(this.main),
            scope,
            this.filterTag,
            this.chartSortBy,
            this.outputType
          );
          const mainDesc = this.isNoSubGroup
            ? ``
            : `-${mainDescriptions.get(Boolean(this.main))}`;
          const boGroupDesc =
            this.boGroupName === '' ? '' : `-${this.boGroupName}`;
          saveAs(
            new Blob([data]),
            `chart-${this.year}年度-${scopeDescriptions.get(
              scope
            )}${mainDesc}${this.categoryDesc()}${boGroupDesc}-${
              this.branchOfficeName
            }${this.outputTypeDesc()}.xlsx`
          );
          this.snackbarMessage = 'ダウンロードが成功しました';
          this.snackbarColor = 'success';
          this.snackbar = true;
        } catch (error) {
          console.error('Download failed:', error);
          this.snackbarMessage = 'ダウンロードが失敗しました';
          this.snackbarColor = 'error';
          this.snackbar = true;
        } finally {
          this.isLoading = false;
        }
      }
    },
    updateViewportHeight() {
      this.viewportHeight = window.innerHeight;
    },
    getSortByDefault(): ChartSortTrainings {
      // 12項目 と ヒヤリハット で 横のソート の デフォルト が異なる
      return this.main ? ChartSortTrainings.DLD : ChartSortTrainings.Default;
    },
    async init() {
      if (this.user) {
        try {
          const token = await this.user.getIdToken();
          this.chartSortBy = this.getSortByDefault();
          // TODO (ohtsuchi) 2025年以降 コードを削除 --- ここから
          if ((this.sortBy as ConfSortByType) === ConfSortByValue.Changed) {
            this.chartSortBy = ChartSortTrainings.IDChar;
          }
          // TODO (ohtsuchi) 2025年以降 コードを削除 --- ここまで
          const d = await Chart.getChartDetail(
            token,
            this.year,
            Boolean(this.main),
            GetScopeFromKey(this.currentScope),
            this.chartSortBy
          );
          console.log(d);
          this.autits.push(...d.header.filter(d => d.isActive));
          this.report = d.column;
          this.updateViewportHeight();
          window.addEventListener('resize', this.updateViewportHeight);

          if (!this.main) {
            this.autits.forEach((header: Audit) => {
              // サーバー側で Training の tags と Category を inner join した結果をフロント側で重複排除
              this.allTags = [
                ...new Map(
                  [...this.allTags, ...header.tags].map(tag => [tag.key, tag]) // key で 重複排除
                ).values(),
              ];
              this.allTags.sort((a: TrainingCategory, b: TrainingCategory) => {
                if (a.order < b.order) return -1;
                if (a.order > b.order) return 1;
                return 0;
              });
            });
          }
        } catch (err) {
          console.log(err);
        }
      }
      this.isMounted = true;
    },
    categoryDesc(): string {
      if (this.filterTag === TrainingCategoryDefault) {
        return '';
      }
      const found = this.allTags.find(
        (c: TrainingCategory) => c.key === this.filterTag
      );
      return found ? `-カテゴリー(${found.name})` : '';
    },
    outputTypeDesc(): string {
      if (this.outputType === ChartOutputType.Default) {
        return '';
      }
      const found = this.outputTypes.find(
        (c: OutputTypeOption) => c.value === this.outputType
      );
      return found ? `-${found.text}` : '';
    },
  },
  async mounted() {
    await this.init();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportHeight);
  },
  data: (): Data => ({
    viewportHeight: 0,
    start: '',
    end: '',
    report: [],
    autits: [
      {
        isActive: true,
        status: 'doing',
        title: '',
        deadline: '名前/期限',
        id: '0',
        safetyEducationIndex: 0,
        tags: [] as TrainingCategory[],
      },
    ],
    isLoading: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: '',
    chartSortBy: ChartSortTrainings.Default,
    outputTypes: [
      {text: '視聴終了時刻', value: ChartOutputType.Default},
      {text: '視聴回数(年度内)', value: ChartOutputType.HistoryCount},
      {text: '視聴回数(累計)', value: ChartOutputType.TotalHistoryCount},
    ],
    outputType: ChartOutputType.Default,
    isMounted: false,
    allTags: [] as TrainingCategory[],
    filterTag: TrainingCategoryDefault,
  }),
});
